import { useFetch } from "@hyper-fetch/react";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { sdk } from "@/api/sdk";
import { Skeleton } from "@/components/ui/skeleton";
import { PLANS } from "@/features/billing/constants/plans";
import { Section, SectionHead } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/** Stripe holds minor units; a price list wants whole ones. */
const money = (amount: number, currency: string) =>
  new Intl.NumberFormat(getLocale(), {
    style: "currency",
    currency,
    maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
  }).format(amount / 100);

/**
 * What the platform costs: three plans, the middle one solid as the one most trainers pick. The
 * plans and their features come from the billing catalogue, the same list the in-app billing page
 * shows; the amounts come from Stripe, so the page cannot quote a price Checkout will not charge.
 * A visitor signs up first - Checkout needs a workspace to bill - so every button goes to sign-up.
 *
 * `headless` on `/pricing`, where the page's own hero already says what this is.
 */
export const PlatformPricing = ({ headless = false }: { headless?: boolean }) => {
  const { data, loading } = useFetch(sdk.checkout.plans.$get);
  const priceOf = new Map((data ?? []).map((price) => [price.plan, price]));

  return (
    <Section id="pricing">
      {headless ? null : (
        <SectionHead
          eyebrow={m.platform_pricing_title()}
          title={m.platform_pricing_heading()}
          aside={m.platform_pricing_aside()}
        />
      )}
      <div className="grid gap-3 md:grid-cols-3">
        {PLANS.map((plan) => {
          const price = priceOf.get(plan.key as "pro" | "premier");
          const featured = plan.key === "pro";
          const soft = featured ? "opacity-75" : "text-muted-foreground";

          return (
            <article
              key={plan.key}
              className={cn(
                "relative flex flex-col rounded-[28px] p-7",
                featured ? "bg-primary text-primary-foreground" : "border border-border bg-card",
              )}
            >
              {featured ? (
                <span className="absolute top-6 right-6 rounded-full bg-primary-foreground px-3 py-1 text-[11px] font-semibold text-primary">
                  {m.platform_pricing_popular()}
                </span>
              ) : null}
              <h3 className="text-2xl font-medium tracking-[-0.03em]">{plan.name}</h3>
              <p className={cn("mt-1.5 pr-24 text-sm", soft)}>{plan.tagline}</p>

              <div className="mt-6 flex min-h-[56px] items-baseline gap-2">
                {plan.key === "free" ? (
                  <span className="text-5xl font-medium tracking-[-0.05em]">{m.platform_pricing_free()}</span>
                ) : loading && !price ? (
                  <Skeleton className="h-11 w-32" />
                ) : price ? (
                  <>
                    <span className="text-5xl font-medium tracking-[-0.05em] tabular-nums">
                      {money(price.amount, price.currency)}
                    </span>
                    <span className={cn("text-sm", soft)}>
                      {price.interval === "year" ? m.platform_pricing_per_year() : m.platform_pricing_per_month()}
                    </span>
                  </>
                ) : (
                  // Billing is not configured, or Stripe did not answer. The plan still shows.
                  <span className={cn("text-sm", soft)}>{m.platform_pricing_at_checkout()}</span>
                )}
              </div>

              <ul className="mt-6 flex flex-1 flex-col gap-2.5">
                {plan.features.map((feature) => (
                  <li key={feature} className={cn("flex gap-2.5 text-sm", soft)}>
                    <span
                      className={cn(
                        "mt-px grid size-[18px] shrink-0 place-items-center rounded-full",
                        featured ? "bg-primary-foreground text-primary" : "bg-primary/15 text-[var(--site-ink)]",
                      )}
                    >
                      <Check aria-hidden className="size-2.5" strokeWidth={3} />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={cn(
                  "mt-7 inline-flex h-11 w-full items-center justify-center rounded-full text-sm font-medium transition-transform active:scale-[0.98]",
                  featured ? "bg-primary-foreground text-primary" : "bg-foreground text-background",
                )}
              >
                {plan.key === "free" ? m.platform_pricing_cta_free() : m.platform_pricing_cta_paid()}
              </Link>
            </article>
          );
        })}
      </div>
    </Section>
  );
};
